import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { Canonical } from './components/Canonical';
import { ScrollOnNavigate } from './components/ScrollOnNavigate';
import { SnapshotProvider } from './data/useSnapshot';
import { HonestNumbers } from './pages/HonestNumbers';
import { Landing } from './pages/Landing';
import { NotFound } from './pages/NotFound';
import { Terminal } from './pages/Terminal';

// `globals.css` is the stylesheet `apps/web` ships, copied verbatim so the two
// deployments cannot drift apart visually. `site.css` is the small amount that
// is only true here: the font variables Next supplies through next/font, and
// the freshness strip, which apps/web has no need for because it reads the
// database directly.
import './globals.css';
import './site.css';

/**
 * Every route renders inside one `<SnapshotProvider>`, so switching pages does
 * not re-fetch and all three always show the same numbers — the alternative is
 * a landing page and a terminal quoting different snapshots, which is exactly
 * the kind of quiet inconsistency the honest-numbers page exists to rule out.
 *
 * Amplify rewrites every unknown path to `index.html` (the rule is in
 * deploy/README.md), so the catch-all route is what a typo'd URL sees: a
 * not-found page, not the landing page pretending the link worked.
 */
// `/terminal/` and `/honest-numbers/` are 301'd to the slashless path by the
// Amplify rules in deploy/README.md. If a host ever serves the page on the
// slash form instead, drop the slash here before the router mounts, so the
// address bar, canonical tag and in-app links all agree on one URL.
const { pathname, search, hash } = window.location;
if (pathname.length > 1 && pathname.endsWith('/')) {
  window.history.replaceState(null, '', `${pathname.replace(/\/+$/, '') || '/'}${search}${hash}`);
}

const root = document.getElementById('root');
if (!root) throw new Error('#root is missing from index.html');

createRoot(root).render(
  <StrictMode>
    <BrowserRouter>
      <ScrollOnNavigate />
      <Canonical />
      <SnapshotProvider>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/terminal" element={<Terminal />} />
          <Route path="/honest-numbers" element={<HonestNumbers />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </SnapshotProvider>
    </BrowserRouter>
  </StrictMode>,
);
